import moment from 'moment';
import formatCycleRuntimeData from '@shared/store/deviceDetail/util/formatCycleRuntimeData';
import initialState from './deviceDetailInitialState';
import mutations from './deviceDetailMutation';
import actions from './deviceDetailAction';

const getters = {
  deviceStatusTableData: (state) => {
    const {
      deviceStatus,
      sensors,
    } = state;

    const newTable = (deviceStatus && deviceStatus.map((status) => {
      const updated = { ...status };
      const sensor = sensors.find((s) => s.serialNumber === status.serialNumber);
      if (sensor) {
        updated.zone = sensor.zone;
        updated.location = sensor.location;
        updated.batteryType = sensor.batteryType;
        updated.ventLocation = sensor.ventLocation;
        updated.accessory = sensor.accessory;
        updated.wired = (sensor.batteryType === 'Wired' ? sensor.batteryType : null);
      }
      return updated;
    })) || [];

    return newTable.sort((a, b) => {
      if (a.did < b.did) {
        return -1;
      }
      if (b.did < a.did) {
        return 1;
      }
      return 0;
    });
  },
  pendingCommandsTableData: (state) => { // make sure its ordered latest first
    const {
      commands,
    } = state;
    const pending = commands.filter((command) => !command.sendTime);
    const sorted = pending.sort((a, b) => {
      if (a.commandTime < b.commandTime) {
        return -1;
      }
      if (a.commandTime > b.commandTime) {
        return 1;
      }
      return 0;
    });
    return sorted;
  },
  completedCommandsTableData: (state) => { // make sure its ordered latest first
    const {
      commands,
    } = state;
    const completed = commands.filter((command) => command.sendTime);
    const sorted = completed.sort((a, b) => {
      if (a.sendTime < b.sendTime) {
        return 1;
      }
      if (a.sendTime > b.sendTime) {
        return -1;
      }
      return 0;
    });
    return sorted;
  },
  cycleChartData: (state) => {
    const {
      startTime,
      startOfDay,
      endOfDay,
      allSupplySerialCycles,
      selectedSupplySerial,
      showOnlyValid,
      weatherData,
      cyclePayloads,
      zoneMapping,
      devices,
    } = state;

    if (!cyclePayloads.length) {
      return {};
    }

    const firmwareDataset = {};

    Object.keys(devices).forEach((sn) => {
      const device = devices[sn];
      const { did, type } = device;
      if (Number(did)) {
        firmwareDataset[`${did}-${type}-temp`] = [];
        firmwareDataset[`${did}-${type}-humidity`] = [];
        firmwareDataset[`${did}-${type}-pressure`] = [];
      }
    });

    const filterTemp = [];
    const comfortTemp = [];
    const atticTemp = [];
    const filterPressure = [];
    const datetime = [];
    const outsideTemp = [];
    const humidity = [];
    let nextDateTime = moment(startTime);
    const datetimeObj = [];

    cyclePayloads.forEach((p, idx) => {
      if (p.devices && p.uploadTime) {
        nextDateTime = nextDateTime.add(5, 'minutes');
        const uploadTime = moment(p.uploadTime);
        const timekey = moment(p.uploadTime).valueOf();
        datetimeObj.push(timekey);
        while (nextDateTime.isBefore(uploadTime)) {
          datetime.push(nextDateTime.toISOString());
          datetimeObj.push(nextDateTime.valueOf());
          nextDateTime = nextDateTime.add(5, 'minutes');
          filterTemp.push(null);
          comfortTemp.push(null);
          atticTemp.push(null);
          filterPressure.push(null);
          outsideTemp.push(null);
          humidity.push(null);
          Object.keys(firmwareDataset).forEach((key) => {
            firmwareDataset[key] = [...firmwareDataset[key], null];
          });
        }
        nextDateTime = uploadTime;
        datetime.push(p.uploadTime);
        const { devices: deviceList } = p;
        const needsToSort = deviceList.filter((d) => d.type !== 'GW_TempHumidity');
        const sortedDevice = needsToSort.sort((a, b) => {
          if (a.did < b.did) {
            return -1;
          }
          if (a.did > b.did) {
            return 1;
          }
          return 0;
        });
        sortedDevice.forEach((d) => {
          const keyT = `${d.deviceId}-${d.type}-temp`;
          const keyP = `${d.deviceId}-${d.type}-pressure`;
          const keyH = `${d.deviceId}-${d.type}-humidity`;
          if (d.temp) {
            firmwareDataset[keyT] = [...firmwareDataset[keyT], d.temp];
          } else {
            firmwareDataset[keyT] = [...firmwareDataset[keyT], null];
          }
          if (d.humidity) {
            firmwareDataset[keyH] = [...firmwareDataset[keyH], d.humidity];
          } else {
            firmwareDataset[keyH] = [...firmwareDataset[keyH], null];
          }
          if (d.pressure) {
            firmwareDataset[keyP] = [...firmwareDataset[keyP], d.pressure];
          } else {
            firmwareDataset[keyP] = [...firmwareDataset[keyP], null];
          }
        });
        const filterSensor = sortedDevice.find((d) => {
          if (selectedSupplySerial) {
            const temp = Number(zoneMapping[selectedSupplySerial].slice(-1)) - 1;
            return Number(d.did) === temp;
          }
          return d.type === 'TempPressure' || d.type === 'A2TempPressure';
        });
        const comfortSensor = sortedDevice.find((d) => {
          if (selectedSupplySerial) {
            const temp = Number(selectedSupplySerial.slice(-1)) - 1;
            return Number(d.did) === temp;
          }
          return d.type === 'Temp' || d.type === 'A2TempHumidity';
        });
        const waterSensor = sortedDevice.find((d) => d.type === 'WaterLeak' || d.type === 'A2WaterLeak');

        const hour = moment(p.uploadTime).hour();
        const date = moment(p.uploadTime).date();
        const key = `${date}-${hour}`;
        if (weatherData[key]) {
          outsideTemp.push(weatherData[key]);
        } else {
          outsideTemp.push(null);
        }
        if (waterSensor) {
          const temp = waterSensor.temp === 0 ? null : waterSensor.temp;
          atticTemp.push(temp);
        } else {
          atticTemp.push(null);
        }
        if (filterSensor) {
          const temp = filterSensor.temp === 0 ? null : filterSensor.temp;
          filterTemp.push(temp);
          filterPressure.push(filterSensor.pressure || null);
        } else {
          filterTemp.push(null);
          filterPressure.push(null);
        }
        if (comfortSensor) {
          const temp = comfortSensor.temp === 0 ? null : comfortSensor.temp;
          comfortTemp.push(temp);
          humidity.push(comfortSensor.humidity);
        } else {
          comfortTemp.push(null);
          humidity.push(null);
        }
      }
    });

    let cycleData = [];
    if (selectedSupplySerial) {
      let cycles = allSupplySerialCycles[selectedSupplySerial];
      if (showOnlyValid) {
        cycles = cycles.filter((c) => c.valid);
      }
      cycleData = formatCycleRuntimeData(cycles, datetimeObj);
    }

    const allKeys = Object.keys(firmwareDataset);
    const maxLength = Math.max(...allKeys.map((key) => firmwareDataset[key].length));

    allKeys.forEach((key) => {
      while (firmwareDataset[key].length < maxLength) {
        firmwareDataset[key].push(null);
      }
    });

    return {
      filterTemp,
      comfortTemp,
      atticTemp,
      firmwareDataset,
      filterPressure,
      cycleData,
      outsideTemp,
      humidity,
      datetime,
      startOfDay,
      endOfDay,
    };
  },
};

const deviceDetailModule = {
  state: initialState(),
  mutations,
  actions,
  getters,
};

export default deviceDetailModule;
